import Controller from '@ember/controller'
import { tracked } from '@glimmer/tracking'
import ENV from 'beebeeboard-booking/config/environment'
import moment from 'moment-timezone';

import {
  action,
  computed,
  get,
  set
} from '@ember/object'

import {
  task,
  timeout
} from 'ember-concurrency'

import { all } from 'rsvp';

import {
  inject as iService
} from '@ember/service'

import { singularize } from 'ember-inflector';
import { debug } from '@ember/debug';

import getConfig from 'beebee-base/lib/get-config';
import { notifyUserError } from 'beebee-base/lib/utility';
export default class ApplicationController extends Controller {
  @iService intl
  @iService session
  @iService powerCalendar
  @iService notify
  @iService store
  @iService log

  queryParams = ['lang', 'embed_type', 'access_token']
  lang = null
  access_token = null

  @tracked showLogin = false
  changeLang = false

  get currentVersion() {
    const version = getConfig(this).APP.version;

    if (version) {
      const [versionNumber, commit] = version.split('+');

      return {
        version: versionNumber,
        commit: commit,
      };
    }

    return null;
  }

  // The ONE normalized form of the active locale, for templates that highlight
  // the current language flag.
  //
  // `lang` reaches this controller two ways with two different casings: as a
  // query param (`?lang=it-IT`, whatever the caller wrote) and as the value
  // `switchLangTask` writes back, which is always lowercased (`locale =
  // lang?.toLowerCase()`). Every flag row in the portal compares it against a
  // fixed literal, so without a normalized reading the comparison is never true
  // after a user actually clicks a flag — the selected language is never marked
  // anywhere. `langFlag` above already lowercases for exactly this reason;
  // this getter exposes the same normalization to templates.
  @computed('lang').readOnly()
  get langCode() {
    return this.lang ? this.lang.toLowerCase() : null;
  }

  @computed('lang').readOnly()
  get langFlag() {
    if (!this.lang) {
      return 'italiano';
    }

    const languageMap = {
      'fr-fr': 'francese',
      'en-us': 'inglese',
      'en-gb': 'inglese',
      'de-de': 'tedesco',
    };

    const lowercaseLang = this.lang.toLowerCase();
    return languageMap[lowercaseLang] || 'italiano';
  }

  @computed('session.option_field').readOnly()
  get option_field() {
     return this.session.option_field || null;
  }

  @computed('content.open_organization.thumbnail_url').readOnly()
  get thumbnail_url() {
    const thumbnailUrl = get(this, 'content.open_organization.thumbnail_url');
    const defaultUrl = 'https://beebeeassets.beebeeboard.com/manifest/icons/beebeeboard/fav_icon-150x150.png';

    return thumbnailUrl ? `${this.urlData}/${thumbnailUrl}-medium.jpeg` : defaultUrl;
  }

  @task({ restartable: true })
  *calculateLocaleTask(locales) {
    const response = yield fetch('https://ipapi.co/json');
    const dataIpApi = yield response.json();
    this.session.setIpLocation(dataIpApi);
    let langIp = dataIpApi?.languages?.split(',')[0];

    const language = langIp || navigator.languages[0] || navigator.language || navigator.userLanguage

    return locales.includes(language.toLowerCase()) ? language : 'it-IT'
  }

  @task({ restartable: true })
  *switchLangTask(lang = null) {
    const locales = ['en-us', 'en-gb', 'it-it', 'fr-fr', 'de-de'];

    yield timeout(50);

    let locale = lang?.toLowerCase();
    if (!locale || !locales.includes(locale)) {
      locale = yield this.calculateLocaleTask.perform(locales);
    }

    yield this.intl.loadTranslations(locale);
    this.intl.setLocale(locale);

    moment.locale(locale);

    if (this.powerCalendar) {
      this.powerCalendar.locale = locale;
    }

    // `set`, not a plain assignment. `lang` is a classic (untracked) controller
    // field — it is a query param, so it cannot simply become `@tracked` — and
    // `langCode`/`langFlag` are `@computed('lang')`. A bare `this.lang = …`
    // therefore updates the value without invalidating anything, and the moment
    // a template DOES consume it in a tracking context Ember throws
    // "You attempted to update …lang … but it is being tracked by a tracking
    // context", aborting the task half-way (the locale is switched, the flag
    // row never repaints). That assertion never fired before only because every
    // flag row in the portal was reading a `lang` that did not exist, so the
    // field was consumed by nothing.
    set(this, 'lang', locale);
    set(this, 'changeLang', false);
  }

  @action
  switchLang(lang, e) {
    get(this, 'switchLangTask').perform(lang)
    e.preventDefault()
  }

  findError(reason) {
    debug(reason)
  }

  tmpSaveLastSavedRecord(record, result = null, operation = 'update') {
    const modelName = record?.constructor?.modelName;
    if (!modelName || !record || record.isDeleted || record.isDeleting) {
      return;
    }

    const lastSavedRecord = this.lastSavedRecord || {};
    const operationData = lastSavedRecord[operation] || {};
    const modelArray = operationData[modelName] || [];

    const id = record.id ? parseInt(record.id) : result?.[0]?.data?.id || null;
    if (!id) return;

    const timestamp = Date.now();
    const updatedData = { id, timestamp };

    set(this, 'lastSavedRecord', {
      ...lastSavedRecord,
      [operation]: {
        ...operationData,
        [modelName]: [...modelArray, updatedData].slice(-5),
      },
    });
  }

  saveRecordRelated(record, notifyUser = true) {
      // Only an Ember Data record can be saved. The public portal binds shared
      // editors to PLAIN OBJECTS as well — the heliguides sign-request form
      // hands `<EditSingleAddress @item={{p}}>` an entry of
      // `PHCustomData.principale`, a POJO of `location_*` fields shaped exactly
      // like `hereToAddress()`'s output, which travels with the form payload
      // instead of being persisted on its own. Reaching `record.save()` with one
      // of those threw "e.save is not a function" in the customer's face
      // (Sentry EMBER-BOOKING-V2, /heliguides_sign_request). Refuse it here
      // instead: loudly in the log, harmlessly for the user, and with the same
      // `false` the refusal path already returns so callers need no new branch.
      const savable =
        record && (typeof record.saveAll === 'function' || typeof record.save === 'function');
      if (!savable) {
        this.log.error(
          'saveRecordRelated called with something that is not a savable record',
          record
        );
        return Promise.resolve(false);
      }

      if (record) {
        if (record.option_json) {
          set(record, 'option', JSON.stringify(record.option_json));
        }
        if (record.modules_json) {
          set(record, 'modules', JSON.stringify(record.modules_json));
        }
      }

      // Not every model implements the bulk `saveAll` (e.g. `address`, saved
      // standalone by the address editor's removeAddressTask). Fall back to a
      // plain `save()` so those records persist instead of throwing
      // "saveAll is not a function". (Sentry EMBER-BOOKING-TS)
      const saveOp =
        typeof record?.saveAll === 'function' ? record.saveAll(record) : record.save();
      return all([saveOp]).then(
        (result) => {
          result.forEach((itemAPI) => {
            // `saveAll()` resolves a JSON:API document; the plain `save()`
            // fallback above resolves the RECORD itself. A record needs no
            // rollback and no pushPayload — it is already in the store and
            // already up to date — so it is a normal outcome, not a malformed
            // payload. Warning on it logged "Payload non valido
            // <model::address:…>" on every standalone address save, i.e. a
            // production issue per save (Sentry EMBER-BOOKING-TZ, route:index).
            if (itemAPI?.constructor?.modelName) {
              return;
            }

            if (!itemAPI?.data?.id || !itemAPI?.data?.type) {
              this.log.warn('Payload non valido', itemAPI);
              return;
            }

            const { id, type } = itemAPI.data;
            const modelName = singularize(type);

            const existing = this.store.peekRecord(modelName, id);
            if (existing) {
              existing.rollbackAttributes();
            }

            this.store.pushPayload(itemAPI);
          });

          if (notifyUser) {
            this.notify.success(this.intl.t('save done'));
          }

          this.tmpSaveLastSavedRecord(record, result);
          this.setOperationSaveTask.perform(result);

          return result;
        },

        (reason) => {
          notifyUserError(this.notify, reason, this.intl);
          return false;
        }
      );
    }

  /**
   * saveRecord — part of the application-controller contract that shared
   * components (e.g. the address editor in beebee-component-address) call.
   * The full app controller in beebee-base exposes it; this public booking
   * portal only had saveRecordRelated, so those shared components crashed with
   * "this.applicationController.saveRecord is not a function". Delegate to the
   * existing related-save path to keep notify / pushPayload / rollback behavior.
   */
  saveRecord(record, notifyUser = true) {
    return this.saveRecordRelated(record, notifyUser);
  }

  /**
   * destroyRecord — contract method used by shared components to delete a
   * record server-side before removing it from the local list. Kept minimal:
   * delete on the server, then flag the destroy operation.
   */
  destroyRecord(record) {
    if (!record) {
      return Promise.resolve(record);
    }

    return record.destroyRecord().then(
      (result) => {
        this.setOperationSaveTask.perform(result, false);
        return result;
      },
      (reason) => {
        notifyUserError(this.notify, reason, this.intl);
        return false;
      }
    );
  }

  @task({ restartable: true })
  *setOperationSaveTask(result, save = true) {
    yield timeout(200);
    set(this, `${save ? 'operationSave' : 'operationDestroy'}`, result);
  }

}
